import { Injectable, OnDestroy, OnInit, inject } from '@angular/core';
import {
    FormGroup,
    FormBuilder,
    FormControl,
    Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable, ReplaySubject, Subject, tap } from 'rxjs';
import { NotificationRef } from '@progress/kendo-angular-notification';
import { QueryObserverResult } from '@tanstack/query-core';
import { SubSink } from 'subsink';
import { RailPerformanceGateway } from '../../../../../gateways/rail-performance/rail-performance-gateway.service';
import { DashboardType } from '../../../constants/dashboard-type';
import { DashboardFilters } from '../../../../../gateways/rail-performance/models/filters/dashboard-filters';
import { NetworkDwellFilters } from '../../../../../gateways/rail-performance/models/filters/network-dwell-filters';
import { RailcarFilters } from '../../../../../gateways/rail-performance/models/filters/railcar-filters';
import { Report } from '../../../../../gateways/rail-performance/models/report/report';
import { CycleFilters } from '../../../../../gateways/rail-performance/models/filters/cycle-filters';
import { EquipmentCountFilters } from '../../../../../gateways/rail-performance/models/filters/equipment-count-filters';
import { Station } from '../../../../shared-platform/models/location/station';
import {
    EntityFilter,
    EntityFilterCollection,
    EntityFilterConstants,
} from '../../../../admin/models/entity-filter';
import { UserService } from '../../../../admin/services/user.service';
import { EntityFilterType } from '../../../../shared-platform/models/constants/entity-filter-type';
import { PersistantStorageService } from '../../../../shared-platform/services/persistant-storage.service';
import { RailStateDataMetric } from '../../../constants/rail-state-metric';
import { PermissionNames } from '../../../../admin/models/permission-names';

@Injectable({
    providedIn: 'root',
})
export class DashboardReportFormService implements OnInit, OnDestroy {
    private formBuilder = inject(FormBuilder);
    private railPerformanceGateway = inject(RailPerformanceGateway);
    private userService = inject(UserService);
    private persistantStorageService = inject(PersistantStorageService);

    form: FormGroup;

    reportQueryResult$?: Observable<QueryObserverResult<Report[], Error>>;

    editNotificationRef?: NotificationRef;
    exportDataNotificationRef?: NotificationRef;

    isSaveEnabled$ = new BehaviorSubject<boolean>(false);
    showFilters$ = new BehaviorSubject<boolean>(false);
    showRSIAIPanel$ = new BehaviorSubject<boolean>(false);
    hasRSIAIAccess$ = new BehaviorSubject<boolean>(false);
    tripsTabActive$ = new BehaviorSubject<boolean>(false);
    showExportMenu$ = new BehaviorSubject<boolean>(true);
    selectedShipper$ = new BehaviorSubject<string>('');

    filtersApplied$ = new ReplaySubject<void>(1);
    exportData$ = new Subject<void>();
    exportDataComplete$ = new Subject<void>();

    showExportDataLoading$ = new BehaviorSubject<boolean>(false);
    railstateMetricToggles$ = new BehaviorSubject<string>(
        RailStateDataMetric.RailcarCounts,
    );

    private sub = new SubSink();

    constructor() {
        this.form = this.formBuilder.group({
            name: new FormControl<string>('', {
                validators: [Validators.required],
            }),
            selectedReport: new FormControl<Report | undefined>(undefined),
            pendingDeleteReport: new FormControl<Report | undefined>(undefined),
            pendingEditReport: new FormControl<Report | undefined>(undefined),
            dashboardType: new FormControl<string>(''),
            filters: this.formBuilder.group({
                common: this.formBuilder.group({
                    customers: new FormControl<EntityFilter | null>(null),
                    countries: new FormControl<string[]>([]),
                    regions: new FormControl<string[]>([]),
                    subRegions: new FormControl<string[]>([]),
                    statesProvinces: new FormControl<string[]>([]),
                    stations: new FormControl<Station[]>([]),
                    commodities: new FormControl<string[]>([]),
                    loaded: new FormControl<string[]>([]),
                }),
                railcar: this.formBuilder.group({
                    motion: new FormControl<string[]>([]),
                }),
                networkDwell: this.formBuilder.group({
                    travelStatus: new FormControl<string[]>([]),
                }),
                equipmentCount: this.formBuilder.group({
                    travelStatus: new FormControl<string[]>([]),
                }),
                cycle: this.formBuilder.group({
                    origin: new FormControl<Station | undefined>(undefined),
                    destination: new FormControl<Station | undefined>(
                        undefined,
                    ),
                }),
            }),
        });

        this.sub.sink = this.filtersFormGroup.valueChanges.subscribe(() => {
            const filters = this.getFiltersByDashboard<any>(this.dashboardType);

            if (this.selectedReport?.filters === JSON.stringify(filters)) {
                this.isSaveEnabled$.next(false);
            } else {
                this.selectedReportFormControl.reset();
                this.isSaveEnabled$.next(this.filtersFormGroup.dirty);
            }
        });

        this.sub.sink = this.selectedReportFormControl.valueChanges.subscribe(
            () => {
                if (!!this.selectedReport) {
                    const selectedFilters: DashboardFilters = JSON.parse(
                        this.selectedReport.filters,
                    );
                    this.setFilterFormGroups(selectedFilters);
                    this.filtersApplied$.next();
                }
            },
        );

        this.sub.sink = this.dashboardTypeFormControl.valueChanges.subscribe(
            () => {
                this.reportQueryResult$ =
                    this.railPerformanceGateway.getAllForType(
                        this.dashboardType,
                    ).result$;
            },
        );

        this.sub.sink = this.commonFiltersFormGroup
            .get('customers')
            ?.valueChanges.subscribe(() => {
                this.applyFilters();
            });
    }

    ngOnInit(): void {}

    private transformNetworkDwellFilters(
        filters: NetworkDwellFilters,
    ): NetworkDwellFilters {
        const travelStatusMap: Record<string, string> = {
            'In Motion': 'In-Transit',
            'In Transit': 'In-Transit',
            'At Destination': 'Destination',
            'At Origin': 'Origin',
            'First Mile': 'Origin',
            'Last Mile': 'Destination',
        };

        const transformedStatus =
            filters.travelStatus?.map(
                (status) => travelStatusMap[status] || status,
            ) || [];

        return {
            ...filters,
            travelStatus: transformedStatus,
        };
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    get nameFormControl(): FormControl<string> {
        return this.form.get('name') as FormControl<string>;
    }

    get selectedReportFormControl(): FormControl<Report | undefined> {
        return this.form.get('selectedReport') as FormControl<
            Report | undefined
        >;
    }

    get selectedReport(): Report | undefined {
        return this.selectedReportFormControl.value;
    }

    get pendingDeleteReportFormControl(): FormControl<Report | undefined> {
        return this.form.get('pendingDeleteReport') as FormControl<
            Report | undefined
        >;
    }

    get pendingDeleteReport(): Report | undefined {
        return this.pendingDeleteReportFormControl.value;
    }

    get pendingEditReportFormControl(): FormControl<Report | undefined> {
        return this.form.get('pendingEditReport') as FormControl<
            Report | undefined
        >;
    }

    get pendingEditReport(): Report | undefined {
        return this.pendingEditReportFormControl.value;
    }

    get dashboardTypeFormControl(): FormControl<string> {
        return this.form.get('dashboardType') as FormControl<string>;
    }

    get dashboardType(): string {
        return this.dashboardTypeFormControl.value;
    }

    get filtersFormGroup(): FormGroup {
        return this.form.get('filters') as FormGroup;
    }

    get commonFiltersFormGroup(): FormGroup {
        return this.filtersFormGroup.get('common') as FormGroup;
    }

    get commonFilters(): DashboardFilters {
        // Ensure the customers value is always a EntityFilterCollection so
        // consumers can reliably access properties like `hasAssociations`.
        const raw = this.commonFiltersFormGroup.getRawValue();
        const result = new DashboardFilters();
        Object.assign(result, raw);

        if (raw.customers instanceof EntityFilterCollection) {
            result.customers = raw.customers;
        } else if (raw.customers instanceof EntityFilter) {
            const customers = [raw.customers];
            result.customers = new EntityFilterCollection(...customers);
        } else if (raw.customers && Array.isArray(raw.customers)) {
            result.customers = new EntityFilterCollection(
                ...raw.customers.map((c: EntityFilter) => EntityFilter.from(c)),
            );
        } else {
            // Handle null or undefined
            result.customers = new EntityFilterCollection();
        }

        return result;
    }

    get railcarFiltersFormGroup(): FormGroup {
        return this.filtersFormGroup.get('railcar') as FormGroup;
    }

    get railcarFilters(): RailcarFilters {
        return {
            ...this.commonFilters,
            ...this.railcarFiltersFormGroup.value,
        };
    }

    get networkDwellFiltersFormGroup(): FormGroup {
        return this.filtersFormGroup.get('networkDwell') as FormGroup;
    }

    get networkDwellFilters(): NetworkDwellFilters {
        return {
            ...this.commonFilters,
            ...this.networkDwellFiltersFormGroup.value,
        };
    }

    get cycleFiltersFormGroup(): FormGroup {
        return this.filtersFormGroup.get('cycle') as FormGroup;
    }

    get cycleFilters(): CycleFilters {
        return {
            ...this.commonFilters,
            ...this.cycleFiltersFormGroup.value,
        };
    }

    get equipmentCountFiltersFormGroup(): FormGroup {
        return this.filtersFormGroup.get('equipmentCount') as FormGroup;
    }

    get equipmentCountFilters(): EquipmentCountFilters {
        return {
            ...this.commonFilters,
            ...this.equipmentCountFiltersFormGroup.value,
        };
    }

    getFiltersByDashboard<T>(dashboardType: string): T {
        switch (dashboardType) {
            case DashboardType.RailcarVolume:
                return this.railcarFilters as T;
            case DashboardType.NetworkDwell:
                return this.transformNetworkDwellFilters(
                    this.networkDwellFilters,
                ) as T;
            case DashboardType.EquipmentCount:
                return this.equipmentCountFilters as T;
            default:
                return this.cycleFilters as T;
        }
    }

    applyFilters() {
        this.persistantStorageService.saveToSession(
            'dashboard-filters' + this.dashboardType,
            this.getFiltersByDashboard(this.dashboardType),
        );
        this.filtersApplied$.next();
    }

    reset() {
        this.pendingDeleteReportFormControl.reset();
        this.pendingEditReportFormControl.reset();
        this.selectedReportFormControl.reset();
    }

    setDashboardType(dashboardType: string) {
        this.reset();
        this.dashboardTypeFormControl.setValue(dashboardType);

        // Try to restore saved filters from session storage FIRST
        const savedFilters = this.persistantStorageService.getFromSession(
            'dashboard-filters' + dashboardType,
        ) as DashboardFilters;

        if (
            savedFilters &&
            savedFilters.customers &&
            savedFilters.customers.length > 0
        ) {
            // Restore saved customer filters
            const customerFilterCollection = new EntityFilterCollection(
                ...savedFilters.customers.map(EntityFilter.from),
            );
            this.setCustomerFilters(customerFilterCollection);
        } else {
            // Fall back to default user group only if no saved filters exist
            this.setCustomerFilterToUserGroup();
        }

        this.serRSIAIUserPermissionForEnable();
        this.showExportMenu$.next(
            dashboardType !== DashboardType.NetworkDwell &&
                dashboardType !== DashboardType.EquipmentCount &&
                dashboardType !== DashboardType.Shipments &&
                dashboardType !== DashboardType.Deliveries &&
                dashboardType !== DashboardType.CycleTime,
        );
    }

    serRSIAIUserPermissionForEnable() {
        this.sub.sink = this.userService.getCurrentUser().subscribe((user) => {
            if (!!user) {
                const isRSIUser = user.hasPermission(
                    PermissionNames.RsiAi.Read,
                );
                const isRailcarVolume =
                    this.dashboardType === DashboardType.RailcarVolume;
                this.hasRSIAIAccess$.next(isRSIUser && isRailcarVolume);
            }
        });
    }

    setCustomerFilterToUserGroup() {
        this.sub.sink = this.userService.getCurrentUser().subscribe((user) => {
            if (!!user && !!user.associations && user.associations.length > 0) {
                const associationFilters = user.associations.map(
                    (association) => {
                        return new EntityFilter(
                            association.acronym,
                            association.name,
                            EntityFilterType.Association,
                        );
                    },
                );

                const distinctAssociationFilters = [
                    ...new Map(
                        associationFilters.map((association) => [
                            association.entityId,
                            association,
                        ]),
                    ).values(),
                ];

                let selectedFilter = [distinctAssociationFilters[0]];

                this.setCustomerFilters(selectedFilter);
            } else {
                this.setCustomerFilters([EntityFilterConstants.Network]);
            }
        });
    }

    setCustomerFilters(customerFilters: EntityFilter[]) {
        const customerFilterCollection = new EntityFilterCollection(
            ...customerFilters,
        );
        this.commonFiltersFormGroup
            .get('customers')
            ?.setValue(customerFilterCollection);
    }

    toggleFiltersVisible() {
        this.showFilters$.next(!this.showFilters$.value);
    }

    toggleRSIAIVisible() {
        this.showRSIAIPanel$.next(!this.showRSIAIPanel$.value);
    }

    setCycleTripsTabActive(tripsTab: boolean) {
        this.tripsTabActive$.next(tripsTab);
    }

    saveNewReport() {
        return this.railPerformanceGateway
            .save({
                name: this.nameFormControl.value,
                type: this.dashboardType,
                filters: JSON.stringify(
                    this.getFiltersByDashboard(this.dashboardType),
                ),
            })
            .pipe(
                tap(() => {
                    this.nameFormControl.reset();
                }),
            );
    }

    saveEditingReport() {
        return this.railPerformanceGateway
            .save({
                id: this.pendingEditReport!.id,
                name: this.pendingEditReport!.name,
                type: this.pendingEditReport!.type,
                filters: JSON.stringify(
                    this.getFiltersByDashboard(this.dashboardType),
                ),
            })
            .pipe(
                tap(() => {
                    this.pendingEditReportFormControl.reset();
                }),
            );
    }

    setPendingReportDelete(report: Report) {
        this.pendingDeleteReportFormControl.setValue(report);
        this.hideAllReportEditMenu();
    }

    setPendingReportEdit(report: Report) {
        const filters: DashboardFilters = JSON.parse(report.filters);
        this.setFilterFormGroups(filters);

        this.showFilters$.next(true);

        this.pendingEditReportFormControl.setValue(report);

        this.hideAllReportEditMenu();
    }

    setFilterFormGroups(filters: DashboardFilters, isReset?: boolean) {
        if (isReset) {
            this.commonFiltersFormGroup.reset();
            this.railcarFiltersFormGroup.reset();
            this.networkDwellFiltersFormGroup.reset();
            this.equipmentCountFiltersFormGroup.reset();
            this.cycleFiltersFormGroup.reset();
            this.setCustomerFilterToUserGroup();
        } else {
            if (Array.isArray(filters?.customers)) {
                filters = {
                    ...filters,
                    customers: new EntityFilterCollection(
                        ...filters.customers.map(EntityFilter.from),
                    ),
                };
            }
            this.commonFiltersFormGroup.patchValue(filters);
            this.railcarFiltersFormGroup.patchValue(filters);
            this.networkDwellFiltersFormGroup.patchValue(filters);
            this.equipmentCountFiltersFormGroup.patchValue(filters);
            this.cycleFiltersFormGroup.patchValue(filters);
        }
        //this.setCustomerFilterToUserGroup();
    }

    cancelPendingReportDelete() {
        this.pendingDeleteReportFormControl.reset();
    }

    cancelPendingReportEdit() {
        this.pendingEditReportFormControl.reset();
    }

    toggleReportListEditMenu(reportToOpen: any) {
        this.sub.sink = this.reportQueryResult$?.subscribe((result) => {
            for (const report of result.data as any[]) {
                if (report.id != reportToOpen.id) {
                    report.show = false;
                }
            }

            reportToOpen.show = !reportToOpen.show;
        });
    }

    hideAllReportEditMenu() {
        this.sub.sink = this.reportQueryResult$?.subscribe((result) => {
            for (const report of result.data as any[]) {
                report.show = false;
            }
        });
    }

    deleteReport() {
        const reportPendingDeleteId = this.pendingDeleteReport?.id;

        return this.railPerformanceGateway.delete(reportPendingDeleteId!).pipe(
            tap(() => {
                this.pendingDeleteReportFormControl.reset();
            }),
        );
    }

    getAllReportsForType(type: string) {
        return this.railPerformanceGateway.getAllForType(type);
    }

    exportData() {
        this.exportData$.next();
    }
}
